// Shared formatters, layout constants, and PropTypes shapes for the Table
// view — split out of TableView.js so it can be reused by TableCells.js,
// TableRows.js and TableHead.js without duplication.
import PropTypes from "prop-types";

import { MACROS, macroCol } from "../../../../../utils/nutrition-progress";
import { MACRO_COLORS, fmtUtc, fmtSleep, ADH_COLOR } from "./viewTokens";

export const DUMBBELL_ICON = "/img/dumbbell.svg";
export const REST_ICON = "/img/svg/rest-icon.svg";
export const DROP_ICON = "/img/new-icons/drop-icon.svg";
export const CHEVRON_ICON = "/img/new-icons/chevron-down.svg";

// Adherence swatch legend next to the toggles — same tokens/labels as
// CalendarLegend.js's SWATCH_ITEMS, kept local here since Table's legend has
// no icon items (workout/photo/period) to share with Calendar's.
export const SWATCH_ITEMS = [
  { key: "onTarget", token: "good" },
  { key: "offSlightly", token: "warn" },
  { key: "offTarget", token: "bad" },
  { key: "notLogged", token: "none" },
];

// Only Protein/Carbs/Fat get a macro-identity color in the header — Fiber and
// kCal stay plain (design). Cell VALUES still color by adherence for all 5
// macros via consumedColor; this only affects the header label.
export const HEAD_MACRO_COLORS = {
  proteins: MACRO_COLORS.proteins,
  carbs: MACRO_COLORS.carbs,
  fats: MACRO_COLORS.fats,
};

// Tail column keys, in display order — same strings as viewTokens.js's
// TABLE_STAT_GROUPS (the "Customise Table" modal's stat keys), so a saved
// template's `columns` array can be checked directly against them.
export const TAIL_KEYS = [
  "bodyweight",
  "steps",
  "sleep",
  "hrv",
  "rhr",
  "trainingReadiness",
  "sessionRPE",
  "waterIntake",
  "bodyFatPercent",
  "days",
];

// activeColumns is null for "Default View" (no template selected — every
// column shows) or a Set of the active template's saved keys otherwise.
export const isColumnVisible = (activeColumns, key) => !activeColumns || activeColumns.has(key);
export const visibleTailKeys = (activeColumns) =>
  activeColumns ? TAIL_KEYS.filter((key) => activeColumns.has(key)) : TAIL_KEYS;

// Body column counts (excluding the Date column) — drive the "no logged days"
// colSpan and the expand-row colSpan, so neither can drift out of sync with
// however many columns a template actually leaves visible. Every group
// (target/consumed/adherence) is MACROS.length wide or 1 wide (default mode's
// adherence column) or entirely absent — the modal has no per-macro
// granularity, only per-group.
export const bodyColsCount = (activeColumns, detailed) => {
  const tailCount = visibleTailKeys(activeColumns).length;
  if (detailed) {
    const groups = ["target", "consumed", "adherence"].filter((key) =>
      isColumnVisible(activeColumns, key)
    );
    return groups.length * MACROS.length + tailCount;
  }
  const consumedCount = isColumnVisible(activeColumns, "consumed") ? MACROS.length : 0;
  const adherenceCount = isColumnVisible(activeColumns, "adherence") ? 1 : 0;
  return consumedCount + adherenceCount + tailCount;
};

export const fmtDayDate = (utcDate) => fmtUtc(utcDate, "ddd, MMM D");
// All numeric table cells are integers, except weight (≤ 1 decimal, no trailing .0).
export const fmtNum = (v) => (v == null ? "–" : Math.round(v).toLocaleString());
export const fmtPct = (v) => (v == null ? "–" : `${Math.round(v)}%`);
// Signed over/under delta (consumed - target) — explicit "+" for over,
// native "-" for under, plain "0" (no sign) exactly on target.
export const fmtSignedNum = (v) => {
  if (v == null) return "–";
  const r = Math.round(v);
  if (r === 0) return "0";
  return r > 0 ? `+${r}` : `${r}`;
};
// Native `title` text for a Detailed-mode Adherence cell in grams mode —
// hovering reveals the % that delta represents (Everfit-style reveal, see
// TableRows.js's DayRow/AvgRow). undefined in % mode (nothing to reveal —
// the cell already shows the %) or when there's no target to measure
// against, same as today: no title attribute at all in either case.
// Shared by DayRow and AvgRow so the condition/copy only lives once.
export const pctOfTargetTitle = (t, adherenceAsPercent, pct) =>
  !adherenceAsPercent && pct != null
    ? t("nutritionPage.progress.table.pctOfTargetTitle", { value: Math.round(pct) })
    : undefined;
export const fmtWeight = (v) => (v == null ? "–" : String(Number(v.toFixed(1))));
// t is required for hrv/rhr (unlike the rest of these formatters) — same
// keys DayDrawerHeader.js already uses for the same fields (common:units.msAbbr,
// restingHeartRate.unit), so a day's HRV/RHR reads identically whether it's
// shown here or in the Day Drawer.
export const fmtHrv = (v, t) => (v == null ? "–" : `${Math.round(v)}${t("common:units.msAbbr")}`);
export const fmtRestingHr = (v, t) => (v == null ? "–" : `${Math.round(v)} ${t("restingHeartRate.unit")}`);
export const fmtReadiness = (v) => (v == null ? "–" : `${Math.round(v)}/25`);
export const fmtBodyFat = (v) => (v == null ? "–" : `${Number(v.toFixed(1))}%`);
// Liters → oz, same factor LogVitalsCard.js uses for the imperial water reading.
export const OZ_PER_L = 33.8140227;

// RPE → RIR (reps in reserve) = 10 − RPE, same formula + 0.5 rounding
// insightsDistributions.js's parseRirNumericForHistogram uses for Program
// Insights' rpe/rir coach preference (no [0,5] clamp there — sessionRPE's own
// range is already 0-10, so the converted RIR naturally stays in range).
export const rpeToRir = (rpe) => Math.round((10 - rpe) * 2) / 2;

// One shared descriptor per tail key (everything in TAIL_KEYS except "days",
// which has no single value/formatter — it renders a workout/rest icon and
// is special-cased directly in DayTail/AvgTail/TailHeadCells instead).
// `field` is the property name on both a day row AND an avgOf() result (the
// two objects use identical field names for every tail stat), so TableCells.js
// can read the same descriptor for a day cell and an average cell — only how
// the value gets WRAPPED differs (TailStat vs AvgText+<td>), not how it's
// read or formatted. `label` is always `(ctx) => string` — most just read
// `ctx.t` against the same i18n keys the "Customise Table" modal already
// uses for these stats (nutritionPage.progress.tableTemplate.stats.*), 2 read
// a coach/unit preference instead (RPE's label follows rpe/rir, pre-resolved
// by TableView.js; Bodyweight/Water's `unit` follows metric/imperial). `unit`
// (only where the header needs a `(unit)` subtitle) drives TableHead.js the
// same way, so a new tail stat is one entry here instead of three
// near-identical render functions across two files. ctx also carries the
// unit-preference closures from TableView.js (displayWt/displayWater/
// displayRpe) for the 3 keys whose VALUE format depends on them.
const statLabel = (key) => (ctx) => ctx.t(`nutritionPage.progress.tableTemplate.stats.${key}`);

export const TAIL_FIELD_CONFIG = {
  bodyweight: { field: "weight", label: statLabel("bodyweight"), unit: (ctx) => ctx.wUnit, format: (v, ctx) => ctx.displayWt(v) },
  steps: { field: "steps", label: statLabel("steps"), format: (v) => fmtNum(v) },
  sleep: { field: "sleep", label: statLabel("sleep"), format: (v) => fmtSleep(v) },
  hrv: { field: "hrv", label: statLabel("hrv"), format: (v, ctx) => fmtHrv(v, ctx.t) },
  rhr: { field: "restingHeartRate", label: statLabel("rhr"), format: (v, ctx) => fmtRestingHr(v, ctx.t) },
  trainingReadiness: { field: "readinessScore", label: statLabel("trainingReadiness"), format: (v) => fmtReadiness(v) },
  sessionRPE: { field: "sessionRPE", label: (ctx) => ctx.rpeLabel, format: (v, ctx) => ctx.displayRpe(v) },
  waterIntake: { field: "waterIntake", label: statLabel("waterIntake"), unit: (ctx) => ctx.waterUnit, format: (v, ctx) => ctx.displayWater(v) },
  bodyFatPercent: { field: "bodyFatPercentage", label: statLabel("bodyFatPercent"), format: (v) => fmtBodyFat(v) },
};

// Color of a consumed macro cell vs its target (none when no target to measure).
export const consumedColor = (day, m) =>
  day.consumed && day.target ? ADH_COLOR[macroCol(day.consumed[m], day.target[m])].text : ADH_COLOR.none.text;

// Left border on a group's first cell — shared by DayRow's consumed macros
// (default mode) and target/consumed/adherence groups (detailed mode).
export const dayCellClass = (i) => `np-table-cell${i === 0 ? " np-table-cell--edge" : ""}`;

export const dayShape = PropTypes.shape({
  utcDate: PropTypes.number,
  logged: PropTypes.bool,
  workout: PropTypes.bool,
  period: PropTypes.bool,
  target: PropTypes.object,
  consumed: PropTypes.object,
  weight: PropTypes.number,
  steps: PropTypes.number,
  sleep: PropTypes.number,
  photos: PropTypes.number,
  hasFoodLog: PropTypes.bool,
  hrv: PropTypes.number,
  restingHeartRate: PropTypes.number,
  readinessScore: PropTypes.number,
  sessionRPE: PropTypes.number,
  waterIntake: PropTypes.number,
  bodyFatPercentage: PropTypes.number,
});

export const avgRowProps = {
  label: PropTypes.string,
  avg: PropTypes.object,
};
